import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";

const field =
  "w-full bg-[#141414] border border-white/10 rounded-lg text-white text-sm px-4 py-3 focus:outline-none focus:border-[#ff5a00] focus:ring-1 focus:ring-[#ff5a00] placeholder:text-white/35 transition-colors";

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("lak_chat"));
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sellerTyping, setSellerTyping] = useState(false);
  const [sellerOnline, setSellerOnline] = useState(null);
  const [showAway, setShowAway] = useState(false);
  const bottomRef = useRef(null);

  const awaySellers = [
    { name: "Ville", phone: "044 354 9300", tel: "0443549300" },
    { name: "Teijo", phone: "0500 653 999", tel: "0500653999" },
  ];

  const hidden = pathname === "/admin" || pathname === "/login";
  const carContext = pathname.startsWith("/autot/") ? document.title : null;

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/chat/${sessionId}`);
      setMessages(data.messages || []);
      setSellerTyping(!!data.seller_typing);
    } catch {
      localStorage.removeItem("lak_chat");
      setSessionId(null);
    }
  }, [sessionId]);

  useEffect(() => { if (open && sessionId) load(); }, [open, sessionId, load]);

  useEffect(() => {
    if (!open || !sessionId) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [open, sessionId, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, sellerTyping]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get("/presence");
        if (alive) setSellerOnline(!!data.online);
      } catch {
        if (alive) setSellerOnline(false);
      }
    };
    check();
    const t = setInterval(check, 12000);
    return () => { alive = false; clearInterval(t); };
  }, [open]);

  useEffect(() => {
    if (!open || sellerOnline !== false) {
      setShowAway(false);
      return;
    }
    const t = setTimeout(() => setShowAway(true), 30000);
    return () => clearTimeout(t);
  }, [open, sellerOnline]);

  const start = async (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim() || !text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/start", {
        name, contact, message: text,
        car: carContext && carContext !== "" ? carContext : null,
      });
      localStorage.setItem("lak_chat", data.session_id);
      setSessionId(data.session_id);
      setMessages(data.messages);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const optimistic = { sender: "customer", text, at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setText("");
    setSending(true);
    try {
      await api.post(`/chat/${sessionId}/message`, { message: optimistic.text });
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle"
        aria-label="Avaa chat"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: "spring" }}
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full flex items-center justify-center bg-[#ff5a00] text-white shadow-lg shadow-[#ff5a00]/30 hover:shadow-xl hover:shadow-[#ff5a00]/40 transition-shadow duration-300"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span key={open ? "x" : "chat"}
            initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.18 }}>
            {open ? <X size={24} /> : <MessageCircle size={24} />}
          </motion.span>
        </AnimatePresence>
        {!open && <span className="absolute top-0 right-0 h-3 w-3 rounded-full bg-[#25D366] border-2 border-[#050505]" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm bg-[#0b0b0b]/95 backdrop-blur-xl border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-2xl shadow-black/60"
            style={{ height: "min(580px, 78vh)" }}
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-b border-white/10 px-5 py-4 flex items-center gap-3">
              <div className="relative h-11 w-11 rounded-full bg-[#ff5a00] flex items-center justify-center shrink-0">
                <MessageCircle size={20} className="text-white" />
                <span className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-[#0f0f0f] ${sellerOnline === false ? "bg-amber-400" : "bg-[#25D366]"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-base tracking-tight text-white leading-tight">Lahden Autokulma</p>
                {sellerOnline === false ? (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400/90 mt-0.5 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Tulemme pian paikalle
                  </p>
                ) : (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#25D366] mt-0.5 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#25D366] animate-pulse" /> Paikalla · vastaamme heti
                  </p>
                )}
              </div>
              <button onClick={() => setOpen(false)} aria-label="Sulje" data-testid="chat-close"
                className="text-white/40 hover:text-white transition-colors p-1">
                <X size={20} />
              </button>
            </div>

            {showAway && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                className="bg-amber-500/10 border-b border-amber-500/30 px-5 py-3" data-testid="chat-away-banner">
                <p className="text-amber-200/90 text-sm leading-relaxed">
                  Emme valitettavasti ole juuri nyt paikalla. Nopeimmin tavoitat meidät soittamalla:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {awaySellers.map((s) => (
                    <a key={s.tel} href={`tel:${s.tel}`} data-testid={`away-call-${s.tel}`}
                      className="inline-flex items-center gap-1.5 bg-amber-500 text-black font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-lg hover:bg-amber-400 transition-colors">
                      {s.name} · {s.phone}
                    </a>
                  ))}
                </div>
              </motion.div>
            )}

            {!sessionId ? (
              <form onSubmit={start} className="flex-1 flex flex-col gap-4 p-5 overflow-y-auto" data-testid="chat-start-form">
                <div>
                  <p className="font-display text-xl tracking-tight text-white">Miten voimme auttaa?</p>
                  <p className="text-white/50 text-sm mt-1 leading-relaxed">
                    Jätä yhteystietosi ja kysymyksesi — myyjämme vastaa sinulle henkilökohtaisesti.
                  </p>
                </div>
                {carContext && (
                  <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                    Aihe: <span className="text-[#ff5a00]">{carContext}</span>
                  </div>
                )}
                <div className="space-y-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">Nimi</label>
                    <input required placeholder="Etunimi Sukunimi" value={name} onChange={(e) => setName(e.target.value)} className={field} data-testid="chat-name" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">Puhelin tai sähköposti</label>
                    <input required placeholder="044 123 4567" value={contact} onChange={(e) => setContact(e.target.value)} className={field} data-testid="chat-contact" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">Viesti</label>
                    <textarea required rows={3} placeholder="Kirjoita kysymyksesi…" value={text} onChange={(e) => setText(e.target.value)} className={field + " resize-none"} data-testid="chat-first-message" />
                  </div>
                </div>
                <button type="submit" disabled={sending} data-testid="chat-start-submit"
                  className="mt-auto inline-flex items-center justify-center gap-2 bg-[#ff5a00] text-white font-mono text-xs uppercase tracking-widest px-6 py-3.5 rounded-lg hover:bg-[#ff7326] transition-colors disabled:opacity-50">
                  {sending ? "Lähetetään…" : <>Aloita keskustelu <Send size={15} /></>}
                </button>
                <p className="text-[10px] text-white/25 text-center leading-relaxed">
                  Tietojasi käytetään vain yhteydenottoon. Emme jaa niitä kolmansille osapuolille.
                </p>
              </form>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-testid="chat-messages">
                  {/* Welcome bubble */}
                  <div className="flex items-end gap-2">
                    <div className="h-7 w-7 rounded-full bg-[#ff5a00] flex items-center justify-center shrink-0 mb-4">
                      <MessageCircle size={14} className="text-white" />
                    </div>
                    <div>
                      <div className="max-w-[220px] px-4 py-2.5 text-sm bg-[#1a1a1a] text-white/90 border border-white/10 rounded-2xl rounded-bl-md">
                        Kiitos viestistäsi! Myyjämme vastaa sinulle pian. 
                      </div>
                    </div>
                  </div>

                  {messages.map((m, i) => {
                    const isCustomer = m.sender === "customer";
                    return (
                      <div key={i} className={`flex items-end gap-2 ${isCustomer ? "justify-end" : "justify-start"}`}>
                        {!isCustomer && (
                          <div className="h-7 w-7 rounded-full bg-[#ff5a00] flex items-center justify-center shrink-0 mb-4">
                            <MessageCircle size={14} className="text-white" />
                          </div>
                        )}
                        <div className={isCustomer ? "text-right" : ""}>
                          <div className={`inline-block max-w-[230px] px-4 py-2.5 text-sm rounded-2xl ${
                            isCustomer
                              ? "bg-[#ff5a00] text-white rounded-br-md"
                              : "bg-[#1a1a1a] text-white/90 border border-white/10 rounded-bl-md"}`}>
                            {m.text}
                          </div>
                          {m.at && <p className="text-[9px] font-mono text-white/25 mt-1 px-1">{fmtTime(m.at)}</p>}
                        </div>
                      </div>
                    );
                  })}
                  {sellerTyping && (
                    <div className="flex items-end gap-2" data-testid="seller-typing">
                      <div className="h-7 w-7 rounded-full bg-[#ff5a00] flex items-center justify-center shrink-0">
                        <MessageCircle size={14} className="text-white" />
                      </div>
                      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                        {[0, 1, 2].map((d) => (
                          <span key={d} className="h-2 w-2 rounded-full bg-white/50 animate-bounce"
                            style={{ animationDelay: `${d * 0.15}s`, animationDuration: "0.9s" }} />
                        ))}
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2 bg-[#0f0f0f]" data-testid="chat-send-form">
                  <input placeholder="Kirjoita viesti…" value={text} onChange={(e) => setText(e.target.value)}
                    className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-full text-white text-sm px-4 py-2.5 focus:outline-none focus:border-[#ff5a00] placeholder:text-white/35 transition-colors"
                    data-testid="chat-input" />
                  <button type="submit" disabled={sending || !text.trim()} aria-label="Lähetä" data-testid="chat-send"
                    className="h-11 w-11 rounded-full flex items-center justify-center bg-[#ff5a00] text-white hover:bg-[#ff7326] transition-colors disabled:opacity-40 shrink-0">
                    <Send size={17} />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
